import { useApolloClient } from '@apollo/client/react'
import {
  Badge,
  Box,
  HStack,
  Icon,
  IconButton,
  Text,
  VStack,
} from '@chakra-ui/react'
import { formatDistanceToNow } from 'date-fns'
import type { ReactNode } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import {
  LuArrowDown,
  LuBell,
  LuCalendar,
  LuCalendarX,
  LuCheck,
  LuCircle,
  LuCircleX,
  LuCrown,
  LuFilePen,
  LuMegaphone,
  LuMessageCircle,
  LuPackage,
  LuShare2,
  LuShieldCheck,
  LuUserCheck,
  LuUserMinus,
  LuUserPlus,
  LuUsers,
} from 'react-icons/lu'

import { useAuth } from '@/auth/useAuth'
import { UserAvatar } from '@/components/atoms/Image/UserAvatar'
import { IRI } from '@/components/molecules/Form/Pooling/Material/types'
import { Tooltip } from '@/components/ui/tooltip'
import { Link } from '@/routing'
import { dateFnsLocaleByAppLocale } from '@/utils/date'
import { contextIriToRoute } from '@/utils/notificationContextIriToRoute'
import {
  buildNotificationLink,
  parseNotificationContext,
} from '@/utils/notificationLink'

import { NotificationScopeBadge } from './NotificationScopeBadge'

export interface NotificationItemProps {
  notification: {
    id: string
    type: string
    context?: unknown
    createdAt: string
    readAt?: string | null
    /** Audience scope: as whom the user received this notification */
    scopeType?: string | null
    scopeIri?: string | null
    scopeName?: string | null
    /** Parameters for translation interpolation */
    params?: Record<string, unknown> | null
  }
  isUnread: boolean
  onMarkAsRead: (id: string, read?: boolean) => void
}

function normalizeNotificationParams(raw: unknown): Record<string, unknown> {
  if (raw == null) return {}
  if (typeof raw === 'string') {
    try {
      const o = JSON.parse(raw)
      return typeof o === 'object' && o !== null && !Array.isArray(o)
        ? (o as Record<string, unknown>)
        : {}
    } catch {
      return {}
    }
  }
  if (typeof raw === 'object' && !Array.isArray(raw)) {
    return raw as Record<string, unknown>
  }
  return {}
}

/** Params from API + string fields from context (ex. periodLabel often stored only on context). */
function buildNotificationInterpolation(
  params: Record<string, unknown>,
  ctx: Record<string, string>
): Record<string, unknown> {
  const out = { ...params }
  for (const [k, v] of Object.entries(ctx)) {
    if (out[k] === undefined && v !== '') {
      out[k] = v
    }
  }
  return out
}

const MATERIAL_OPTION_IN_APP_TYPES = new Set([
  'material_option_low_stock',
  'material_option_projected_shortage',
  'material_option_restock_week_reminder',
])

/** Scrollable body so long messages are not truncated (replaces lineClamp). */
const NOTIFICATION_BODY_SCROLL_PROPS = {
  flex: 1,
  minW: 0,
  maxH: 'min(320px, 50vh)',
  overflowY: 'auto',
  overflowX: 'hidden',
  fontSize: 'xs',
  color: 'fg.muted',
  lineHeight: '1.45',
} as const

const MATERIAL_OPTION_RICH_COMPONENTS = {
  strong: <Box as="strong" fontWeight="semibold" display="inline" />,
  em: <Box as="em" fontStyle="italic" display="inline" />,
}

type IconComponent = typeof LuMegaphone
const NOTIFICATION_ICON: Record<
  string,
  { icon: IconComponent; color: string; colorDark: string }
> = {
  announcement: {
    icon: LuMegaphone,
    color: 'purple.500',
    colorDark: 'purple.300',
  },
  booking_request: {
    icon: LuCalendar,
    color: 'orange.500',
    colorDark: 'orange.300',
  },
  recurring_booking_request: {
    icon: LuCalendar,
    color: 'orange.500',
    colorDark: 'orange.300',
  },
  booking_confirmed: {
    icon: LuCheck,
    color: 'green.500',
    colorDark: 'green.300',
  },
  booking_canceled: {
    icon: LuCalendarX,
    color: 'red.500',
    colorDark: 'red.300',
  },
  booking_refused: { icon: LuCircleX, color: 'red.500', colorDark: 'red.300' },
  counter_proposal: {
    icon: LuFilePen,
    color: 'teal.500',
    colorDark: 'teal.300',
  },
  message: { icon: LuMessageCircle, color: 'blue.500', colorDark: 'blue.300' },
  discussion: {
    icon: LuMessageCircle,
    color: 'blue.500',
    colorDark: 'blue.300',
  },
  insurance_certificate_validated: {
    icon: LuShieldCheck,
    color: 'green.500',
    colorDark: 'green.300',
  },
  circle_join_request: {
    icon: LuUserPlus,
    color: 'blue.500',
    colorDark: 'blue.300',
  },
  circle_join_request_cancelled: {
    icon: LuUserMinus,
    color: 'orange.500',
    colorDark: 'orange.300',
  },
  network_circle_join_request: {
    icon: LuShare2,
    color: 'brand.600',
    colorDark: 'brand.300',
  },
  network_community_joined: {
    icon: LuUsers,
    color: 'teal.500',
    colorDark: 'teal.300',
  },
  circle_membership_accepted: {
    icon: LuUserCheck,
    color: 'green.500',
    colorDark: 'green.300',
  },
  circle_membership_kicked: {
    icon: LuCircleX,
    color: 'red.500',
    colorDark: 'red.300',
  },
  circle_promoted_to_admin: {
    icon: LuShieldCheck,
    color: 'purple.500',
    colorDark: 'purple.300',
  },
  circle_promoted_to_owner: {
    icon: LuCrown,
    color: 'brand.fg',
    colorDark: 'brand.300',
  },
  circle_demoted_from_admin: {
    icon: LuArrowDown,
    color: 'gray.500',
    colorDark: 'gray.400',
  },
  circle_demoted_to_member: {
    icon: LuArrowDown,
    color: 'gray.500',
    colorDark: 'gray.400',
  },
  raw: { icon: LuMegaphone, color: 'purple.500', colorDark: 'purple.300' },
  material_option_low_stock: {
    icon: LuPackage,
    color: 'amber.500',
    colorDark: 'amber.300',
  },
  material_option_projected_shortage: {
    icon: LuPackage,
    color: 'orange.500',
    colorDark: 'orange.300',
  },
  material_option_restock_week_reminder: {
    icon: LuPackage,
    color: 'teal.500',
    colorDark: 'teal.300',
  },
}
const DEFAULT_ICON = { icon: LuBell, color: 'gray.500', colorDark: 'gray.400' }
function getNotificationIcon(type: string) {
  return NOTIFICATION_ICON[type] ?? DEFAULT_ICON
}

export function NotificationItem({
  notification,
  isUnread,
  onMarkAsRead,
}: NotificationItemProps) {
  const { t, i18n } = useTranslation('notifications')
  const { user } = useAuth()
  const locale = dateFnsLocaleByAppLocale(i18n.language ?? 'en')
  const ctx = parseNotificationContext(notification.context)
  const rawParams = normalizeNotificationParams(notification.params)
  const interpolation = buildNotificationInterpolation(rawParams, ctx)
  const isMaterialOptionInApp = MATERIAL_OPTION_IN_APP_TYPES.has(
    notification.type
  )

  // Get user gender for context-aware translations
  const genderContext =
    user?.gender === 'male'
      ? 'male'
      : user?.gender === 'female'
        ? 'female'
        : user?.gender === 'nonBinary'
          ? 'nonBinary'
          : undefined
  const isAnnouncement =
    notification.type === 'announcement' || notification.type === 'raw'
  const {
    icon: IconComponent,
    color: iconColor,
    colorDark: iconColorDark,
  } = getNotificationIcon(notification.type)

  const typeLabel =
    t(`types.${notification.type}`, {
      defaultValue: notification.type,
    }) || notification.type

  // For announcements, title and message are directly in params
  // For other types, build translation keys from notification type: e.g., "circle_membership_accepted.title"
  const isAnnouncementType =
    notification.type === 'announcement' || notification.type === 'raw'
  const hasDirectParams =
    isAnnouncementType &&
    notification.params &&
    typeof notification.params === 'object' &&
    'title' in notification.params &&
    'message' in notification.params

  let displayTitle: string
  let displayMessage: string
  let displayMessageRich: ReactNode | null = null

  if (hasDirectParams && notification.params) {
    // For announcements, use params.title and params.message directly
    displayTitle = (notification.params.title as string) || t('unknown.title')
    displayMessage =
      (notification.params.message as string) || t('unknown.message')
  } else if (notification.type) {
    // For other types, use translation keys built from type
    const titleKey = `${notification.type}.title`
    const messageKey = `${notification.type}.message`

    const tOpts = {
      ...interpolation,
      ...(genderContext ? { context: genderContext } : {}),
    }

    displayTitle = t(titleKey, {
      ...tOpts,
      defaultValue: t('unknown.title'),
    })

    if (isMaterialOptionInApp) {
      displayMessage = ''
      displayMessageRich = (
        <Trans
          t={t}
          i18nKey={messageKey}
          values={interpolation}
          context={genderContext}
          components={MATERIAL_OPTION_RICH_COMPONENTS}
        />
      )
    } else {
      displayMessage = t(messageKey, {
        ...tOpts,
        defaultValue: t('unknown.message'),
      })
    }
  } else {
    // Fallback to unknown
    displayTitle = t('unknown.title')
    displayMessage = t('unknown.message')
  }

  const dateLabel = formatDistanceToNow(new Date(notification.createdAt), {
    addSuffix: true,
    locale,
  })

  // Link destination + carried scope (single source of truth, shared with the toast).
  const linkTo = buildNotificationLink(notification)
  const linkRoute: { to: string } | null = linkTo ? { to: linkTo } : null

  // Distinct styles for announcements: purple accent; others: orange
  const borderColor = isAnnouncement ? 'purple.400' : 'orange.400'
  const badgeColor = isAnnouncement ? 'purple' : 'gray'

  const cardContent = (
    <>
      <VStack align="stretch" gap={1}>
        <HStack justify="space-between" align="flex-start" gap={2}>
          <HStack gap={2} flex={1} minW={0}>
            <Icon
              as={IconComponent}
              boxSize="4"
              color={iconColor}
              _dark={{ color: iconColorDark }}
              flexShrink={0}
              aria-hidden
            />
            <Text fontWeight={isUnread ? 'semibold' : 'normal'} fontSize="sm">
              {displayTitle}
            </Text>
          </HStack>
          <HStack gap={1}>
            {isUnread ? (
              <Tooltip
                content={t('markAsRead')}
                positioning={{ placement: 'top' }}
              >
                <IconButton
                  aria-label={t('markAsRead')}
                  size="xs"
                  variant="ghost"
                  onMouseDown={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    onMarkAsRead(notification.id)
                  }}
                >
                  <Box
                    as="span"
                    display="inline-block"
                    w="3"
                    h="3"
                    borderRadius="full"
                    bg="currentColor"
                  />
                </IconButton>
              </Tooltip>
            ) : (
              <Tooltip
                content={t('actions.markAsUnread')}
                positioning={{ placement: 'top' }}
              >
                <IconButton
                  aria-label={t('actions.markAsUnread')}
                  size="xs"
                  variant="ghost"
                  onMouseDown={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                  }}
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    onMarkAsRead(notification.id, false)
                  }}
                >
                  <Icon as={LuCircle} />
                </IconButton>
              </Tooltip>
            )}
          </HStack>
        </HStack>
        <HStack gap={2} align="flex-start">
          {ctx.authorId &&
            (() => {
              const authorRoute = contextIriToRoute(ctx.authorId)
              const avatar = (
                <UserAvatar
                  userId={ctx.authorId as IRI<'users', string>}
                  size="sm"
                />
              )
              // Don't make avatar a link if the card itself is already a link (to avoid nested <a> tags)
              if (authorRoute && !linkRoute) {
                return (
                  <Link
                    to={authorRoute.to}
                    onClick={(e) => {
                      e.stopPropagation()
                    }}
                    style={{
                      display: 'inline-block',
                      flexShrink: 0,
                      marginTop: '2px',
                    }}
                  >
                    {avatar}
                  </Link>
                )
              }
              return (
                <Box flexShrink={0} mt="0.5">
                  {avatar}
                </Box>
              )
            })()}
          {isAnnouncement ? (
            <Box {...NOTIFICATION_BODY_SCROLL_PROPS}>
              <Text as="span" whiteSpace="pre-wrap" display="block">
                {displayMessage}
              </Text>
            </Box>
          ) : (
            <Box {...NOTIFICATION_BODY_SCROLL_PROPS}>
              {displayMessageRich ? (
                <Box as="div">{displayMessageRich}</Box>
              ) : (
                <Text as="span" whiteSpace="pre-wrap" display="block">
                  {displayMessage}
                </Text>
              )}
            </Box>
          )}
        </HStack>
        <HStack gap={2} flexWrap="wrap" align="center" justify="space-between">
          <HStack gap={2} align="center" minW={0}>
            <Text as="span" fontSize="xs" color="fg.muted" flexShrink={0}>
              {dateLabel}
            </Text>
            <NotificationScopeBadge
              scopeType={notification.scopeType}
              scopeIri={notification.scopeIri}
              scopeName={notification.scopeName}
            />
            <Badge
              size="sm"
              variant="subtle"
              colorPalette={badgeColor}
              flexShrink={0}
            >
              {typeLabel}
            </Badge>
          </HStack>
        </HStack>
      </VStack>
    </>
  )

  const handleCardClick = () => {
    // Mark as read when clicking on the card
    if (isUnread) {
      onMarkAsRead(notification.id)
    }
    // Navigation is handled by the Link component or RedirectView
  }

  const boxProps = {
    py: 2,
    px: 3,
    borderRadius: 'lg',
    bg: isUnread ? (isAnnouncement ? 'purple.50' : 'blackAlpha.50') : undefined,
    _dark: {
      bg: isUnread
        ? isAnnouncement
          ? 'purple.950/30'
          : 'whiteAlpha.50'
        : undefined,
    },
    borderLeftWidth: isUnread ? '2px' : 0,
    borderLeftColor: borderColor,
    ...(linkRoute
      ? {
          as: Link,
          to: linkRoute.to,
          onClick: handleCardClick,
          cursor: 'pointer',
          _hover: {
            bg: isUnread
              ? isAnnouncement
                ? 'purple.100'
                : 'blackAlpha.100'
              : 'blackAlpha.50',
            _dark: {
              bg: isUnread
                ? isAnnouncement
                  ? 'purple.950/40'
                  : 'whiteAlpha.100'
                : 'whiteAlpha.50',
            },
          },
          transition: 'background-color 0.2s',
        }
      : {}),
  }

  return <Box {...boxProps}>{cardContent}</Box>
}
